import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { Btn } from '../chancey';
import { useChanceyTheme } from '../../theme/ThemeContext';
import {
  useSubscriptionStore,
  type SubscriptionState,
} from '../../stores/subscriptionStore';
import { getPlatform, type NormalizedPackage } from '../../services/purchasesService';
import { track } from '../../services/analyticsService';
import { safeAreaPaddingX, withSafeArea } from '../layout/safeArea';
import type { PaywallReason } from './usePaywall';
import { friendlyErrorMessage } from '../../utils/errorCopy';

interface PaywallSheetProps {
  reason: PaywallReason;
  onClose: () => void;
}

interface CopyContext {
  resetAtLocal: string | null;
  used: number | null;
  cap: number | 'unlimited' | null;
}

interface CopyBundle {
  eyebrow: (ctx: CopyContext) => string;
  title: string;
  body: (ctx: CopyContext) => string;
}

const COPY: Record<PaywallReason, CopyBundle> = {
  scan_cap: {
    eyebrow: ({ used, cap }) => {
      const usedText = used ?? '—';
      const capText = cap === 'unlimited' ? '∞' : (cap ?? '—');
      return `You scanned ${usedText} of ${capText} free tickets`;
    },
    title: 'Unlimited scans with\u00a0Pro',
    body: ({ resetAtLocal, used, cap }) => {
      const usedText = used ?? '—';
      const capText = cap === 'unlimited' ? '∞' : (cap ?? '—');
      if (resetAtLocal) {
        return `Your free ${usedText}/${capText} monthly scans reset ${resetAtLocal}. Chancey Pro removes the limit so you can scan every ticket, every draw.`;
      }
      return `You've used ${usedText} of ${capText} free ticket scans this month. Chancey Pro removes the limit so you can scan every ticket, every draw.`;
    },
  },
  upgrade: {
    eyebrow: () => 'Go Pro',
    title: 'Scan every ticket,\u00a0every draw',
    body: ({ used, cap }) => {
      const capText = cap === 'unlimited' ? '∞' : (cap ?? 5);
      if (typeof used === 'number' && typeof capText === 'number') {
        return `Free plays include ${capText} scans per month — you've used ${used}. Chancey Pro removes the limit and keeps your ticket history in sync across devices.`;
      }
      return `Free plays include ${capText} scans per month. Chancey Pro removes the limit and keeps your ticket history in sync across devices.`;
    },
  },
};

function formatResetAt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return d.toUTCString();
  }
}

function periodLabel(iso: string | null): string {
  if (!iso) return '';
  // ISO 8601 period: "P1M", "P1Y", "P1W" etc.
  if (iso === 'P1M') return '/month';
  if (iso === 'P1Y') return '/year';
  if (iso === 'P1W') return '/week';
  if (iso === 'P6M') return '/6 months';
  return ` / ${iso.replace(/^P/, '').toLowerCase()}`;
}

export const PaywallSheet: React.FC<PaywallSheetProps> = ({ reason, onClose }) => {
  const { c, typ, dark } = useChanceyTheme();
  const history = useHistory();
  const { status, offerings, error, scansUsedThisMonth, scansCapThisMonth, scansResetAtUtc, refresh, purchase, restore } =
    useSubscriptionStore(
      useShallow((s: SubscriptionState) => ({
        status: s.status,
        offerings: s.offerings,
        error: s.error,
        scansUsedThisMonth: s.scansUsedThisMonth,
        scansCapThisMonth: s.scansCapThisMonth,
        scansResetAtUtc: s.scansResetAtUtc,
        refresh: s.refresh,
        purchase: s.purchase,
        restore: s.restore,
      }))
    );

  const firstRender = useRef(true);
  const viewTracked = useRef(false);
  useEffect(() => {
    if (!viewTracked.current) {
      viewTracked.current = true;
      track('paywall_view', { source: reason });
    }
    if (firstRender.current && (status === 'unknown' || status === 'error' || !offerings)) {
      firstRender.current = false;
      void refresh();
    }
  }, [status, offerings, refresh, reason]);

  const [busy, setBusy] = useState<'subscribe' | 'restore' | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [picked, setPicked] = useState<'monthly' | 'annual'>('annual');
  const busyRef = useRef(false);

  const resetAtLocal = useMemo(() => formatResetAt(scansResetAtUtc), [scansResetAtUtc]);
  const copy = COPY[reason];

  const monthly = offerings?.monthly ?? null;
  const annual = offerings?.annual ?? null;
  const activePkg: NormalizedPackage | null = picked === 'monthly' ? monthly : annual;
  const loading = status === 'loading' && !offerings;

  const onSubscribe = async () => {
    if (!activePkg || busyRef.current) return;
    busyRef.current = true;
    setBusy('subscribe');
    setLocalError(null);
    track('purchase_start', { source: picked });
    try {
      const info = await purchase(activePkg);
      if (info.isPro) {
        track('purchase_success', { source: picked, outcome: 'pro' });
        onClose();
      } else {
        track('purchase_failure', { source: picked, outcome: 'not_entitled', errorCode: 'not_entitled' });
        setLocalError('Purchase completed but the entitlement is not active yet. Try again in a moment.');
      }
    } catch (err) {
      const message = errorMessage(err);
      track('purchase_failure', { source: picked, outcome: message ? 'error' : 'cancelled', errorCode: errorCode(err) });
      setLocalError(message);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const onRestore = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('restore');
    setLocalError(null);
    track('purchase_start', { source: 'restore' });
    try {
      const info = await restore();
      if (info.isPro) {
        track('purchase_success', { source: 'restore', outcome: 'pro' });
        onClose();
      } else {
        track('purchase_failure', { source: 'restore', outcome: 'not_found', errorCode: 'not_found' });
        setLocalError('No active Chancey Pro subscription found on this account.');
      }
    } catch (err) {
      track('purchase_failure', { source: 'restore', outcome: 'error', errorCode: errorCode(err) });
      setLocalError(errorMessage(err));
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const closeIfIdle = () => {
    if (busyRef.current) return;
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col justify-end bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Chancey Pro paywall"
      onClick={closeIfIdle}
    >
      <style>{`
        @keyframes paywallSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
      <div
        className="rounded-t-[28px] px-[22px] pt-3"
        style={{
          ...safeAreaPaddingX(22),
          background: c.bg,
          animation: 'paywallSlideUp 0.32s cubic-bezier(.2,.8,.2,1)',
          paddingBottom: `max(24px, ${withSafeArea('bottom')})`,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pb-3">
          <div className="h-[5px] w-10 rounded-md" style={{ background: c.line }} />
        </div>

        <div
          className="rounded-[22px] p-[18px] text-white"
          style={{ background: c.gradient, boxShadow: `0 18px 48px ${c.accent}30` }}
        >
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-90"
            style={{ fontFamily: typ.body }}
          >
            {copy.eyebrow({ resetAtLocal, used: scansUsedThisMonth, cap: scansCapThisMonth })}
          </div>
          <div
            className="mt-1 text-[26px] leading-tight"
            style={{ fontFamily: typ.display, fontWeight: typ.displayWeight, letterSpacing: typ.displayLetter }}
          >
            {copy.title}
          </div>
          <div
            className="mt-2 text-[13px] leading-snug opacity-95"
            style={{ fontFamily: typ.body }}
          >
            {copy.body({ resetAtLocal, used: scansUsedThisMonth, cap: scansCapThisMonth })}
          </div>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="space-y-2">
              <div className="h-[70px] w-full rounded-[16px]" style={{ background: c.surfaceAlt }} />
              <div className="h-[70px] w-full rounded-[16px]" style={{ background: c.surfaceAlt }} />
            </div>
          ) : status === 'error' && !offerings ? (
            <div
              className="rounded-[14px] border p-3 text-[13px]"
              style={{ borderColor: c.line, color: c.ink, background: c.surface, fontFamily: typ.body }}
            >
              {error ?? 'Could not load subscription options.'}
              <button
                type="button"
                className="ml-2 font-semibold underline"
                onClick={() => void refresh()}
                style={{ color: c.accent }}
              >
                Retry
              </button>
            </div>
          ) : !offerings || (!monthly && !annual) ? (
            <div
              className="rounded-[14px] border p-3 text-[13px]"
              style={{ borderColor: c.line, color: c.ink, background: c.surface, fontFamily: typ.body }}
            >
              Chancey Pro subscription options are still syncing.
              <button
                type="button"
                className="ml-2 font-semibold underline"
                onClick={() => void refresh()}
                style={{ color: c.accent }}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {annual ? (
                <PackageRow
                  label="Yearly"
                  priceLine={`${annual.priceFormatted}${periodLabel(annual.periodIso)}`}
                  subline={monthly ? annualSavings(annual, monthly) : 'Best value'}
                  selected={picked === 'annual'}
                  onSelect={() => setPicked('annual')}
                  badge={monthly ? 'Save 33%' : null}
                />
              ) : null}
              {monthly ? (
                <PackageRow
                  label="Monthly"
                  priceLine={`${monthly.priceFormatted}${periodLabel(monthly.periodIso)}`}
                  subline="Cancel anytime"
                  selected={picked === 'monthly'}
                  onSelect={() => setPicked('monthly')}
                  badge={null}
                />
              ) : null}
            </div>
          )}
        </div>

        {localError ? (
          <div
            className="mt-3 rounded-[12px] border px-3 py-2 text-[12px]"
            style={{ borderColor: c.lucky, color: c.lucky, background: `${c.lucky}10`, fontFamily: typ.body }}
          >
            {localError}
          </div>
        ) : null}

        <div className="mt-4">
          <Btn
            c={c}
            typ={typ}
            variant="lucky"
            style={{ width: '100%' }}
            disabled={!activePkg || busy !== null}
            onClick={() => void onSubscribe()}
          >
            {busy === 'subscribe' ? 'Processing…' : `Subscribe${activePkg ? ` · ${activePkg.priceFormatted}` : ''}`}
          </Btn>
        </div>

        <div className="mt-3 flex items-center justify-between text-[12px]" style={{ fontFamily: typ.body }}>
          <button
            type="button"
            className="font-semibold underline"
            style={{ color: c.ink }}
            onClick={() => void onRestore()}
            disabled={busy !== null}
          >
            {busy === 'restore' ? 'Restoring…' : 'Restore purchases'}
          </button>
          <button
            type="button"
            className="font-medium"
            style={{ color: c.inkSoft }}
            onClick={closeIfIdle}
            disabled={busy !== null}
          >
            Not now
          </button>
        </div>

        <ComplianceFooter c={c} typ={typ} history={history} onClose={onClose} />

        {/* Subtle tint so dark-mode safe-area strip matches sheet */}
        {dark ? null : <div className="h-0" />}
      </div>
    </div>
  );
};

function annualSavings(annual: NormalizedPackage, monthly: NormalizedPackage): string {
  const annualMicros = annual.priceMicros;
  const monthlyMicros = monthly.priceMicros;
  if (!annualMicros || !monthlyMicros) return 'Best value';
  const twelveX = monthlyMicros * 12;
  if (twelveX <= annualMicros) return 'Best value';
  const savedPct = Math.round(((twelveX - annualMicros) / twelveX) * 100);
  if (savedPct <= 0) return 'Best value';
  return `Save ${savedPct}% vs monthly`;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    // purchases-js throws { errorCode, message } with userCancelled; squelch cancels.
    const code = (err as unknown as { errorCode?: number }).errorCode;
    if (code === 1 /* UserCancelledError */) return '';
    return friendlyErrorMessage(err, 'Purchase failed.');
  }
  return 'Purchase failed.';
}

function errorCode(err: unknown): string {
  if (err instanceof Error) return (err as unknown as { errorCode?: number }).errorCode?.toString() ?? err.name;
  return 'unknown';
}

const PackageRow: React.FC<{
  label: string;
  priceLine: string;
  subline: string;
  selected: boolean;
  badge: string | null;
  onSelect: () => void;
}> = ({ label, priceLine, subline, selected, badge, onSelect }) => {
  const { c, typ } = useChanceyTheme();
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 rounded-[16px] border px-3.5 py-3 text-left"
      style={{
        borderColor: selected ? c.accent : c.line,
        background: selected ? `${c.accent}12` : c.surface,
      }}
    >
      <div
        aria-hidden
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2"
        style={{ borderColor: selected ? c.accent : c.inkMuted }}
      >
        {selected ? (
          <div className="h-2.5 w-2.5 rounded-full" style={{ background: c.accent }} />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div
            className="text-[15px] font-semibold"
            style={{ color: c.ink, fontFamily: typ.body }}
          >
            {label}
          </div>
          {badge ? (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
              style={{ background: c.accent, color: c.accentInk, fontFamily: typ.body }}
            >
              {badge}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-[12px]" style={{ color: c.inkSoft, fontFamily: typ.body }}>
          {subline}
        </div>
      </div>
      <div
        className="text-right text-[15px] font-semibold"
        style={{ color: c.ink, fontFamily: typ.body, fontVariantNumeric: 'tabular-nums' }}
      >
        {priceLine}
      </div>
    </button>
  );
};

const ComplianceFooter: React.FC<{
  c: ReturnType<typeof useChanceyTheme>['c'];
  typ: ReturnType<typeof useChanceyTheme>['typ'];
  history: ReturnType<typeof useHistory>;
  onClose: () => void;
}> = ({ c, typ, history, onClose }) => {
  const platform = getPlatform();
  const cancelLocation =
    platform === 'ios'
      ? 'your App Store subscription settings'
      : platform === 'android'
        ? 'your Google Play subscription settings'
        : 'your account settings';

  const go = (path: string) => {
    onClose();
    history.push(path);
  };

  return (
    <div
      className="mt-4 text-[11px] leading-relaxed"
      style={{ color: c.inkMuted, fontFamily: typ.body }}
    >
      Chancey Pro auto-renews until canceled. Cancel anytime from {cancelLocation}. Payment is charged to your account at purchase. By subscribing you agree to our{' '}
      <button
        type="button"
        onClick={() => go('/terms')}
        className="underline"
        style={{ color: c.inkSoft }}
      >
        Terms
      </button>{' '}
      and{' '}
      <button
        type="button"
        onClick={() => go('/privacy')}
        className="underline"
        style={{ color: c.inkSoft }}
      >
        Privacy Policy
      </button>
      .
    </div>
  );
};
